import Debit from '@/models/Debit';
import NedarimStandingOrderDtoIn from '@/models/NedarimStandingOrderDtoIn';
import { aptDebitToStandingOrder, isAptEndingSoon } from '@/pages/Donation/StandingOrders/aptStandingOrders';
import {
  AptOrderOverride,
  applyOverride,
  bankActionUrl,
  canCancelBank,
  canFreezeBank,
  canReactivateBank,
  getBankOrderState,
} from '@/pages/Donation/StandingOrders/bankStandingOrderActions';
import { BoardKind } from '@/pages/Donation/StandingOrders/boardColumns';
import { StandingOrderActionResult } from '@/pages/Donation/StandingOrders/standingOrderActions';

/*
 * «הוראות קבע לפי תורם» in the management area (HAT-10024). The office asked for it because a bank
 * return can cover several collections of one donor at once - orders in several funds - and the
 * coordinator's board shows one fund at a time: «צריך להכין בניהול מקום לטפל בהו"ק לפי תורם - לא לפי
 * פרוייקט כמו אצל הרכזים, ובמקרה של כמה הו"ק של התורם נוכל לסדר את כולן ביחד» (21.10.2026).
 *
 * The screen is the coordinators' bank board (boardColumns: the same columns, filters, summary and
 * export; the same blade and dialogs, HAT-1441), with the donor as the way in, the fund on each row,
 * and several orders changed in one go.
 */

/** Must match FeatureFlagKeys.DonorBankStandingOrders and SeedFeatureFlag_DonorBankStandingOrders.sql. */
export const DONOR_BANK_STANDING_ORDERS_FEATURE_KEY = 'donor-bank-standing-orders';

export const DONORS_SEARCH_URL = 'management/donor-bank-standing-orders/donors';
export const DONORS_WARM_URL = 'management/donor-bank-standing-orders/warm';
export const donorOrdersUrl = (contactId: number): string => `management/donor-bank-standing-orders/${contactId}/orders`;

/** One donor the search found - management/donor-bank-standing-orders/donors. */
export interface DonorSearchResult {
  contactId: number;
  firstName: string;
  lastName: string;
  israeliId: string;
  phone: string;
  city: string;
  ordersCount: number;
  fundsCount: number;
}

export const donorFullName = (donor: Pick<DonorSearchResult, 'firstName' | 'lastName'>): string =>
  `${donor.firstName} ${donor.lastName}`.trim();

/** One of the donor's orders, as the bank board draws it, and the fund it collects for. */
export type DonorOrder = NedarimStandingOrderDtoIn & {
  fundId: number;
  fundName: string | null;
};

/** The bank board's answers to the questions the boards answer differently (boardColumns.BoardKind). */
export const BANK_BOARD: BoardKind = { isBank: true, needsSecondPill: isAptEndingSoon };

/** An order's id is APT's, unique across funds; the fund is part of what the row is all the same. */
export const rowKey = (order: DonorOrder): string => `${order.fundId}-${order.standingOrderId}`;

export const sameOrder = (a: DonorOrder, b: DonorOrder): boolean =>
  a.fundId === b.fundId && a.standingOrderId === b.standingOrderId;

/** «קרן 130» where APT has no name for the fund. */
export const fundLabel = (order: Pick<DonorOrder, 'fundId' | 'fundName'>, t: (key: string, options?: Record<string, unknown>) => string): string =>
  order.fundName ?? t('donorStandingOrders.columns.fundNumber', { id: order.fundId });

/**
 * The server's rows as board rows. An override is what the server answered after an action, kept
 * until the Azure copy of APT catches up (bankStandingOrderActions.applyOverride).
 */
export function toDonorOrders(debits: Debit[], overrides: Record<number, AptOrderOverride> = {}, now: Date = new Date()): DonorOrder[] {
  return debits.map((debit) => ({
    ...aptDebitToStandingOrder(applyOverride(debit, overrides[debit.donationId], now.getTime()), now),
    fundId: debit.fundId,
    fundName: debit.fundName?.trim() || null,
  }));
}

/** The actions several orders can take together. Editing is one order at a time: each has its own amount. */
export type BulkActionKind = 'freeze' | 'reactivate' | 'cancel';

export const BULK_ACTION_KINDS: BulkActionKind[] = ['freeze', 'reactivate', 'cancel'];

/** Whether the board would offer this action on the order by itself - the same rules as the row menu. */
export function canTakeBulk(order: DonorOrder, kind: BulkActionKind): boolean {
  const state = getBankOrderState(order);
  switch (kind) {
    case 'freeze': return canFreezeBank(state);
    case 'reactivate': return canReactivateBank(state, order);
    case 'cancel': return canCancelBank(state);
  }
}

export interface BulkPlan {
  /** The selected orders the action applies to, in the order they were selected. */
  eligible: DonorOrder[];
  /** Selected orders the action does not apply to - a frozen order asked to freeze, say. Left as they are, and said so. */
  skipped: DonorOrder[];
  /**
   * Selected orders the action would apply to, but whose collection the office has locked before
   * sending it to the bank (the Masav lock, HAT-1441). Held, as the row menu holds them.
   */
  locked: { order: DonorOrder; until: Date }[];
}

export function planBulk(
  orders: DonorOrder[],
  kind: BulkActionKind,
  lockOf: (order: DonorOrder) => Date | null = () => null,
): BulkPlan {
  const plan: BulkPlan = { eligible: [], skipped: [], locked: [] };
  for (const order of orders) {
    if (!canTakeBulk(order, kind)) { plan.skipped.push(order); continue; }
    const until = lockOf(order);
    if (until) plan.locked.push({ order, until });
    else plan.eligible.push(order);
  }
  return plan;
}

export type BulkRowStatus = 'waiting' | 'running' | 'done' | 'failed';

export interface BulkOutcome {
  order: DonorOrder;
  status: BulkRowStatus;
  /** The server's answer on success - the order as it now reads. */
  result?: StandingOrderActionResult;
  /** Why it failed, in the coordinator's terms. */
  error?: string;
}

/**
 * Runs the action on each order in turn - one request each, the same request the row menu sends -
 * and reports after every one. In turn rather than all at once: each write holds the office's APT
 * for a moment, and a refusal on one order (changed meanwhile, a collection locked) must not stop
 * the others. The outcomes are a new array on every step so React sees the change.
 */
export async function runBulk(
  orders: DonorOrder[],
  kind: BulkActionKind,
  reason: string,
  post: (url: string, body: object) => Promise<StandingOrderActionResult>,
  describeError: (error: unknown) => string,
  refusedText: (result: StandingOrderActionResult) => string,
  onProgress?: (outcomes: BulkOutcome[]) => void,
): Promise<BulkOutcome[]> {
  let outcomes: BulkOutcome[] = orders.map((order) => ({ order, status: 'waiting' }));
  const report = (index: number, patch: Partial<BulkOutcome>) => {
    outcomes = outcomes.map((o, i) => (i === index ? { ...o, ...patch } : o));
    onProgress?.(outcomes);
  };

  const body = kind !== 'reactivate' && reason.trim() ? { reason: reason.trim() } : {};

  for (let i = 0; i < orders.length; i++) {
    const order = orders[i];
    report(i, { status: 'running' });
    try {
      const result = await post(bankActionUrl(order.fundId, order.standingOrderId, kind), body);
      if (result.success) report(i, { status: 'done', result });
      else report(i, { status: 'failed', error: refusedText(result) });
    } catch (error) {
      report(i, { status: 'failed', error: describeError(error) });
    }
  }

  return outcomes;
}

/** How many orders in how many funds - the donor's line above the board. */
export function countFunds(orders: readonly DonorOrder[]): { orders: number; funds: number } {
  return { orders: orders.length, funds: new Set(orders.map((o) => o.fundId)).size };
}
